import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  LibraryBooks as LibraryBooksIcon,
  Search as SearchIcon,
  Image as ImageIcon,
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
  BarChart as BarChartIcon,
  PersonSearch as PersonSearchIcon,
  WarningAmber as WarningAmberIcon,
  SmartToy as SmartToyIcon,
  ToggleOn as ToggleOnIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { en } from '../translations/en';
import GettyTab from '../components/admin/GettyTab';
import SearchTab from '../components/admin/SearchTab';
import ProcessingTab from '../components/admin/ProcessingTab';
import UserExportTab from '../components/admin/UserExportTab';
import LegalDocsTab from '../components/admin/LegalDocsTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import UserLookupTab from '../components/admin/UserLookupTab';
import IncidentBannerTab from '../components/admin/IncidentBannerTab';
import AiCatalogingTab from '../components/admin/AiCatalogingTab';
import FeatureFlagsTab from '../components/admin/FeatureFlagsTab';
import CouponsTab from '../components/admin/CouponsTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      {...other}
      sx={{ py: 3 }}
    >
      {value === index && children}
    </Box>
  );
}

const Admin: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        sx={{
          mb: 4,
          color: theme.palette.primary.main,
          fontFamily: 'Libre Baskerville, serif',
          fontWeight: 'bold',
        }}
      >
        {en.admin.title}
      </Typography>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 2 }}
          >
            <Tab
              label={en.admin.tabs.getty}
              icon={<LibraryBooksIcon />}
              iconPosition="start"
            />
            <Tab
              label={en.admin.tabs.search}
              icon={<SearchIcon />}
              iconPosition="start"
            />
            <Tab
              label={en.admin.tabs.processing}
              icon={<ImageIcon />}
              iconPosition="start"
            />
            <Tab
              label={en.admin.tabs.userExport}
              icon={<FileDownloadIcon />}
              iconPosition="start"
            />
            <Tab
              label={en.admin.tabs.legalDocs}
              icon={<DescriptionIcon />}
              iconPosition="start"
            />
            <Tab
              label={en.admin.tabs.analytics}
              icon={<BarChartIcon />}
              iconPosition="start"
            />
            <Tab
              label={en.admin.tabs.userLookup}
              icon={<PersonSearchIcon />}
              iconPosition="start"
            />
            <Tab
              label={en.admin.tabs.incidentBanner}
              icon={<WarningAmberIcon />}
              iconPosition="start"
            />
            <Tab
              label="AI Cataloging"
              icon={<SmartToyIcon />}
              iconPosition="start"
            />
            <Tab
              label="Feature Flags"
              icon={<ToggleOnIcon />}
              iconPosition="start"
            />
            <Tab
              label="Coupons"
              icon={<LocalOfferIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <GettyTab />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <SearchTab />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ProcessingTab />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box sx={{ p: 3 }}>
            <UserExportTab />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Box sx={{ p: 3 }}>
            <LegalDocsTab />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <AnalyticsTab />
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <UserLookupTab />
        </TabPanel>

        <TabPanel value={activeTab} index={7}>
          <IncidentBannerTab />
        </TabPanel>

        <TabPanel value={activeTab} index={8}>
          <AiCatalogingTab />
        </TabPanel>

        <TabPanel value={activeTab} index={9}>
          <FeatureFlagsTab />
        </TabPanel>

        <TabPanel value={activeTab} index={10}>
          <CouponsTab />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Admin;
